"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { StatusCard } from "@/components/ui/StatusCard";
import { reloadOnceForChunkError } from "@/lib/chunks/chunkErrors";

// Same pattern as app/error.tsx — React error boundaries catch render errors
// before window.onerror, so posthog.captureException must be called
// explicitly alongside console.error (react AGENTS.md).
export default function BlogError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  useEffect(() => {
    console.error(error);
    posthog.captureException(error);
    // unstable_retry() re-renders against the same stale chunk URL and can never
    // recover a ChunkLoadError (TRA-346) — only a fresh navigation re-fetches the
    // build manifest. Deferred + cooldown-guarded; falls through to the card below
    // within the cooldown window (see reloadOnceForChunkError's own doc comment).
    reloadOnceForChunkError(error);
  }, [error]);

  return (
    <main className="flex-1 flex items-center justify-center px-6 py-14">
      <Container size="narrow">
        <StatusCard
          tone="error"
          title="Something went wrong"
          body="We hit an unexpected error loading the blog. Please try again."
          actions={
            <>
              <Button onClick={() => unstable_retry()}>Try again</Button>
              <Button variant="secondary" href="/blog">
                Back to Safari Blog
              </Button>
            </>
          }
        />
      </Container>
    </main>
  );
}
